import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useCalendars } from './CalendarContext';
import {
    getFinishScore,
    recordLast10Entry,
    setFinishModeActivated,
    recordLast10Completion,
    recordLast10Abandonment,
    calcDaysStalled,
    isStalled
} from '../services/last10Storage';

const Last10Context = createContext();

export const useLast10 = () => {
    const context = useContext(Last10Context);
    if (!context) {
        throw new Error('useLast10 must be used within a Last10Provider');
    }
    return context;
};

// Threshold config
export const LAST10_THRESHOLD = 80;        // % at which Last 10% state activates
export const STALL_THRESHOLD_DAYS = 7;     // days stalled before flagged as abandoned

export const Last10Provider = ({ children }) => {
    const { currentUser } = useAuth();
    const { selectedCalendar } = useCalendars();

    const [finishScore, setFinishScore] = useState({
        score: null,
        totalEnteredLast10: 0,
        totalCompletedFromLast10: 0,
        totalAbandonedInLast10: 0
    });

    // ID of the goal currently in Finish Mode (full-focus overlay)
    const [finishModeGoalId, setFinishModeGoalId] = useState(null);

    const [loadingScore, setLoadingScore] = useState(false);

    // ── Load user finish score ─────────────────────────────────────────────

    const loadFinishScore = useCallback(async () => {
        if (!currentUser || !selectedCalendar) return;
        try {
            setLoadingScore(true);
            const scoreData = await getFinishScore(currentUser.uid, selectedCalendar.id);
            setFinishScore(scoreData);
        } catch (err) {
            console.error('Error loading Finish Score:', err);
        } finally {
            setLoadingScore(false);
        }
    }, [currentUser, selectedCalendar]);

    useEffect(() => {
        loadFinishScore();
    }, [loadFinishScore]);

    // ── Last 10% state detection ───────────────────────────────────────────

    /**
     * Called by GoalCard when it detects completionPercentage >= 80 for the
     * first time (goal.last10EnteredAt is not yet set).
     */
    const enterLast10 = useCallback(async (goalId, completionPercentage, calendarId) => {
        if (!currentUser || !goalId) return;
        const targetCalendarId = calendarId || selectedCalendar?.id;
        if (!targetCalendarId) {
            console.error('Calendar ID is required for Last 10% tracking');
            return;
        }
        try {
            await recordLast10Entry(goalId, currentUser.uid, targetCalendarId, completionPercentage);
            await loadFinishScore();
        } catch (err) {
            console.error('Error recording Last 10% entry:', err);
        }
    }, [currentUser, selectedCalendar, loadFinishScore]);

    // ── Finish Mode ────────────────────────────────────────────────────────

    const activateFinishMode = useCallback(async (goalId) => {
        if (!currentUser || !goalId) return;
        try {
            await setFinishModeActivated(goalId, true, currentUser.uid);
            setFinishModeGoalId(goalId);
        } catch (err) {
            console.error('Error activating Finish Mode:', err);
        }
    }, [currentUser]);

    const deactivateFinishMode = useCallback(async (goalId) => {
        if (!goalId) {
            setFinishModeGoalId(null);
            return;
        }
        try {
            await setFinishModeActivated(goalId, false, currentUser?.uid);
        } catch (err) {
            console.error('Error deactivating Finish Mode:', err);
        } finally {
            setFinishModeGoalId(null);
        }
    }, [currentUser]);

    // ── Completion hook ────────────────────────────────────────────────────

    /**
     * Call this when a goal's status is set to 'completed' AND it was in
     * Last 10% state (goal.last10EnteredAt is truthy).
     */
    const handleGoalCompletedFromLast10 = useCallback(async (goal) => {
        if (!currentUser || !goal?.last10EnteredAt || !goal?.calendarId) return;
        try {
            await recordLast10Completion(goal.id, currentUser.uid, goal.calendarId);
            await loadFinishScore();
        } catch (err) {
            console.error('Error recording Last 10% completion:', err);
        }
    }, [currentUser, loadFinishScore]);

    // ── Abandonment check ──────────────────────────────────────────────────

    /**
     * Evaluate a list of goals and flag any that have stalled in Last 10%.
     * Designed to be called once after goals load (e.g. from GoalContext).
     */
    const checkAndFlagAbandonment = useCallback(async (goals) => {
        if (!currentUser || !goals?.length) return;
        const stalled = goals.filter(g =>
            g.last10EnteredAt &&
            !g.completedFromLast10 &&
            !g.abandonedInLast10 &&
            g.status !== 'completed' &&
            isStalled(g, STALL_THRESHOLD_DAYS)
        );

        for (const goal of stalled) {
            if (!goal.calendarId) continue;
            try {
                const days = calcDaysStalled(goal);
                await recordLast10Abandonment(goal.id, currentUser.uid, goal.calendarId, days);
            } catch (err) {
                console.error('Error flagging abandonment for goal', goal.id, err);
            }
        }

        if (stalled.length > 0) {
            await loadFinishScore();
        }
    }, [currentUser, loadFinishScore]);

    // ── Utility helpers ────────────────────────────────────────────────────

    const isInLast10 = (progress, goal) => {
        if (!progress || !goal) return false;
        const pct = progress.completionPercentage ?? 0;

        // Check if completion % is >= 80% OR if only 1 task remains
        const percentageThresholdMet = pct >= LAST10_THRESHOLD;
        const onlyOneTaskRemaining = progress.totalTasks > 0 &&
            (progress.totalTasks - progress.completedTasks) === 1;

        return (percentageThresholdMet || onlyOneTaskRemaining) && pct < 100 && goal.status !== 'completed';
    };

    const getMicrocopy = (completionPercentage) => {
        if (completionPercentage >= 97) return 'Almost there. Do not stop now.';
        if (completionPercentage >= 92) return 'You are this close. Close it out.';
        if (completionPercentage >= 88) return 'Most people quit here. You are not most people.';
        return 'Final phase. Everything else can wait.';
    };

    return (
        <Last10Context.Provider value={{
            // Score data
            finishScore,
            loadingScore,
            refreshFinishScore: loadFinishScore,

            // Finish Mode state
            finishModeGoalId,
            activateFinishMode,
            deactivateFinishMode,

            // Actions
            enterLast10,
            handleGoalCompletedFromLast10,
            checkAndFlagAbandonment,

            // Helpers
            isInLast10,
            getMicrocopy,

            // Config
            LAST10_THRESHOLD,
            STALL_THRESHOLD_DAYS
        }}>
            {children}
        </Last10Context.Provider>
    );
};
